import { useEffect, useRef, useState } from 'react';
import { Badge, Card, Text, Title } from '@tremor/react';
import { Link } from 'react-router-dom';
import { fetchCustomersRFM } from '../api/customersRfm';
import { fetchQuotes } from '../api/quotes';
import { fetchSalesReturns } from '../api/returns';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import type { Quote, RFMCustomer, RFMSegment, SalesReturnRow } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 });

const SEGMENT_COLOR: Record<RFMSegment, 'emerald' | 'blue' | 'sky' | 'rose' | 'amber' | 'gray' | 'slate'> = {
  champions: 'emerald',
  loyal: 'blue',
  recent: 'sky',
  big_at_risk: 'rose',
  at_risk: 'amber',
  dormant: 'gray',
  regular: 'slate',
};

function daysAgoStr(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

/**
 * v2.35 commercial account scorecard — research blueprint §4.4, composed
 * client-side from three sources the app already serves: the account's RFM
 * row (12-month behavior), the open-quote book filtered to this anum, and
 * the last 90 days of sales returns. Each source is independent and
 * error-tolerant: a 403/503 on one hides only its tiles.
 */
export default function CustomerScorecard({ anum }: { anum: number }) {
  const [rfm, setRfm] = useState<RFMCustomer | null | undefined>(undefined); // undefined=loading, null=absent
  const [quotes, setQuotes] = useState<Quote[] | null>(null);
  const [returns, setReturns] = useState<SalesReturnRow[] | null>(null);

  // Lazy gate: these 3 sources are each a full daemon scan (RFM over all
  // accounts, every quote since 2000, 90d of returns), and the daemon is
  // single-threaded — so don't fire them until the card is actually scrolled
  // into view. Falls back to eager load where IntersectionObserver is absent
  // (e.g. jsdom in tests), preserving the original behavior there.
  const cardRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (inView) return;
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setInView(true); obs.disconnect(); }
    }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [inView]);

  useEffect(() => {
    if (!inView) return;
    let alive = true;
    fetchCustomersRFM(365)
      .then((p) => { if (alive) setRfm(p.customers.find((c) => c.anum === anum) ?? null); })
      .catch(() => { if (alive) setRfm(null); });
    fetchQuotes({ from: '2000-01-01', to: localYMD(), aging: 'all', limit: 2000 })
      .then((rows) => { if (alive) setQuotes(rows.filter((q) => q.anum === anum)); })
      .catch(() => { if (alive) setQuotes(null); });
    fetchSalesReturns({ from: daysAgoStr(90), to: localYMD(), limit: 5000 })
      .then((rows) => { if (alive) setReturns(rows.filter((r) => r.anum === anum)); })
      .catch(() => { if (alive) setReturns(null); });
    return () => { alive = false; };
  }, [anum, inView]);

  const quotesTotal = (quotes ?? []).reduce((a, q) => a + q.subt_mxn, 0);
  const returnsTotal = Math.abs((returns ?? []).reduce((a, r) => a + r.sell_mxn, 0));

  return (
    <Card ref={cardRef}>
      <div className="flex flex-wrap items-baseline gap-x-3">
        <Title className="text-base">{t.customers.scorecard.title}</Title>
        <Text className="text-tremor-content text-xs">{t.customers.scorecard.window}</Text>
      </div>

      {rfm === undefined ? (
        <div className="mt-3 h-12 bg-tremor-background-muted rounded animate-pulse" />
      ) : (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <div>
            <div className="text-tremor-content text-xs">{t.customers.scorecard.segment}</div>
            {rfm ? (
              <div className="mt-0.5">
                <Badge color={SEGMENT_COLOR[rfm.segment]}>{t.customers.segments[rfm.segment]}</Badge>
                <span className="ml-2 text-sm text-tremor-content tabular-nums">{rfm.r}·{rfm.f}·{rfm.m}</span>
                {rfm.churn_risk && (
                  <div className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">⚠ {t.customers.churnBadge}</div>
                )}
              </div>
            ) : (
              <div className="text-sm text-tremor-content mt-0.5">{t.customers.scorecard.noRfm}</div>
            )}
          </div>

          {rfm && (
            <>
              <Tile
                label={t.customers.scorecard.ticketsMonth}
                value={(rfm.tickets / 12).toFixed(1)}
                sub={`${rfm.tickets} · 12m`}
              />
              <Tile
                label={t.customers.scorecard.avgTicket}
                value={fmtMxn.format(rfm.tickets > 0 ? rfm.revenue_mxn / rfm.tickets : 0)}
                sub={fmtMxn.format(rfm.revenue_mxn) + ' · 12m'}
              />
              <Tile
                label={t.customers.scorecard.lastBuy}
                value={rfm.last_date}
                sub={t.customers.scorecard.daysAgo.replace('{n}', String(rfm.recency_days))}
              />
              <Tile
                label={t.customers.scorecard.cadence}
                value={rfm.cadence_days > 0
                  ? t.customers.scorecard.cadenceUnit.replace('{n}', rfm.cadence_days.toFixed(0))
                  : '—'}
              />
            </>
          )}

          {quotes !== null && (
            <div>
              <div className="text-tremor-content text-xs">{t.customers.scorecard.openQuotes}</div>
              <div className="text-tremor-content-strong text-lg font-semibold tabular-nums">
                {quotes.length}
                {quotes.length > 0 && <span className="text-sm font-normal"> · {fmtMxn.format(quotesTotal)}</span>}
              </div>
              {quotes.length > 0 && (
                <Link to="/sales/cotizaciones" className="text-xs text-tremor-brand hover:underline">
                  {t.nav.sales} →
                </Link>
              )}
            </div>
          )}

          {returns !== null && (
            <Tile
              label={t.customers.scorecard.returns90}
              value={fmtMxn.format(returnsTotal)}
              sub={t.customers.scorecard.returnsDetail.replace('{n}', String(returns.length))}
              warn={returns.length > 0}
            />
          )}
        </div>
      )}
    </Card>
  );
}

function Tile({ label, value, sub, warn }: { label: string; value: string; sub?: string; warn?: boolean }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className={'text-lg font-semibold tabular-nums ' +
        (warn ? 'text-amber-700 dark:text-amber-400' : 'text-tremor-content-strong')}>
        {value}
      </div>
      {sub && <div className="text-xs text-tremor-content">{sub}</div>}
    </div>
  );
}
